import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { client } from '../../../app/services/api/orderManagementClient';
import { applyRoundAndFormatDecimal } from '../../../utils';
import {
  parseAiOrderExplanation,
  ParsedAiOrderExplanation,
} from './aiOrderDecisionSnapshot';

interface AiOrderExplanationButtonProps {
  translationNamespace: 'orderForm' | 'orderDetails';
  recommendedQuantity?: number | null;
  finalQuantity?: number | null;
  isQuantityOverridden?: boolean;
  primaryUom?: string;
  orderableUom?: string;
  storeNumber?: string;
  orderId?: number | null;
  orderItemId?: number | null;
  onClick?: React.MouseEventHandler<HTMLElement>;
  triggerVariant?: 'pill' | 'help';
  inline?: boolean;
}

const formatNumber = (value?: number | null) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return applyRoundAndFormatDecimal(value);
};

const quantitiesDiffer = (left?: number | null, right?: number | null) =>
  left != null && right != null && Math.abs(left - right) > 0.0001;

const AiOrderExplanationButton: React.FC<AiOrderExplanationButtonProps> = ({
  translationNamespace,
  recommendedQuantity,
  finalQuantity,
  isQuantityOverridden = false,
  primaryUom = '',
  orderableUom = '',
  storeNumber,
  orderId,
  orderItemId,
  onClick,
  triggerVariant = 'pill',
  inline = false,
}) => {
  const { t } = useTranslation(translationNamespace);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [explanation, setExplanation] = useState<ParsedAiOrderExplanation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForecast, setShowForecast] = useState(false);
  const [hasInlineLoaded, setHasInlineLoaded] = useState(false);

  const canSelfLoad = Boolean(storeNumber && orderId != null && orderItemId != null);
  const canOpen = Boolean(inline || onClick || canSelfLoad);
  const open = Boolean(anchorEl);

  const recommendation = explanation?.recommendedQuantity ?? recommendedQuantity;
  const displayedFinalQuantity = finalQuantity ?? recommendation;
  const quantityWasOverridden = isQuantityOverridden || explanation?.isQuantityOverridden === true;
  const displayOrderUom = orderableUom || primaryUom;
  const hasForecast = Boolean(explanation?.projection?.length);
  const hasCalculationBreakdown = Boolean(
    explanation?.targetStockLevel != null &&
    explanation.projectedStockOnHand != null &&
    explanation.rawOrderQuantity != null,
  );

  const capturedInputs = useMemo(() => {
    if (!explanation) return [];

    return [
      { label: t('aiExplanationStockOnHand'), value: explanation.stockOnHand, uom: primaryUom },
      { label: t('aiExplanationIncoming'), value: explanation.stockOnOrder, uom: primaryUom },
      { label: t('aiExplanationForecastDemand'), value: explanation.predictedDemandQuantity, uom: primaryUom },
      { label: t('aiExplanationSafetyStock'), value: explanation.safetyStock, uom: primaryUom },
      { label: t('aiExplanationMinimumStock'), value: explanation.minStockOnHandQuantity, uom: primaryUom },
      { label: t('aiExplanationMaximumStock'), value: explanation.maxStockOnHandQuantity, uom: primaryUom },
      { label: t('aiExplanationMaximumOrder'), value: explanation.maxOrderQuantity, uom: primaryUom },
    ].filter((row) => row.value != null);
  }, [explanation, primaryUom, t]);

  const calculationSteps = useMemo(() => {
    if (!explanation || !hasCalculationBreakdown) return [];

    const steps = [
      {
        label: t('aiExplanationTargetStock'),
        value: `${formatNumber(explanation.targetStockLevel)} ${primaryUom}`.trim(),
        detail: explanation.effectiveSafetyStock != null && explanation.replenishmentCycleDays != null
          ? t('aiExplanationTargetStockDetail', {
            minimum: formatNumber(explanation.minStockOnHandQuantity),
            safety: formatNumber(explanation.effectiveSafetyStock),
            days: formatNumber(explanation.replenishmentCycleDays),
            uom: primaryUom,
          })
          : undefined,
      },
      {
        label: t('aiExplanationProjectedStock'),
        value: `${formatNumber(explanation.projectedStockOnHand)} ${primaryUom}`.trim(),
      },
      {
        label: t('aiExplanationOrderNeeded'),
        value: `${formatNumber(explanation.rawOrderQuantity)} ${primaryUom}`.trim(),
        detail: t('aiExplanationOrderNeededDetail', {
          target: formatNumber(explanation.targetStockLevel),
          stock: formatNumber(explanation.projectedStockOnHand),
          uom: primaryUom,
        }),
      },
    ];

    if (quantitiesDiffer(explanation.rawOrderQuantity, explanation.orderQuantityAfterMaxStock)) {
      steps.push({
        label: t('aiExplanationMaxStockApplied'),
        value: `${formatNumber(explanation.orderQuantityAfterMaxStock)} ${primaryUom}`.trim(),
        detail: undefined,
      });
    }

    if (quantitiesDiffer(explanation.orderQuantityAfterMaxStock, explanation.orderQuantityAfterMaxOrder)) {
      steps.push({
        label: t('aiExplanationMaxOrderApplied'),
        value: `${formatNumber(explanation.orderQuantityAfterMaxOrder)} ${primaryUom}`.trim(),
        detail: undefined,
      });
    }

    if (explanation.orderableQuantityBeforeRounding != null && displayOrderUom) {
      steps.push({
        label: t('aiExplanationCaseRounding'),
        value: `${formatNumber(recommendation)} ${displayOrderUom}`.trim(),
        detail: t('aiExplanationCaseRoundingDetail', {
          before: formatNumber(explanation.orderableQuantityBeforeRounding),
          after: formatNumber(recommendation),
          orderUom: displayOrderUom,
          primaryQuantity: formatNumber(explanation.recommendedQuantityInPrimaryUom),
          primaryUom,
        }),
      });
    }

    return steps;
  }, [
    displayOrderUom,
    explanation,
    hasCalculationBreakdown,
    primaryUom,
    recommendation,
    t,
  ]);

  const close = () => {
    setAnchorEl(null);
    setShowForecast(false);
  };

  const loadExplanation = useCallback(async () => {
    if (!canSelfLoad || explanation || loading) return;

    setLoading(true);
    setError('');
    try {
      const response = await client.getAiOrderExplanation(storeNumber, orderId!, orderItemId!);
      const parsedExplanation = parseAiOrderExplanation(response.data);
      if (response.success && parsedExplanation) {
        setExplanation(parsedExplanation);
      } else {
        setError(t('aiExplanationNotAvailable'));
      }
    } catch {
      setError(t('aiExplanationLoadError'));
    } finally {
      setLoading(false);
    }
  }, [canSelfLoad, explanation, loading, orderId, orderItemId, storeNumber, t]);

  useEffect(() => {
    if (inline && !hasInlineLoaded) {
      setHasInlineLoaded(true);
      void loadExplanation();
    }
  }, [hasInlineLoaded, inline, loadExplanation]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      onClick(event);
      return;
    }

    setAnchorEl(event.currentTarget);
    void loadExplanation();
  };

  if (!canOpen) return null;

  const content = (
    <Box
      role={inline ? 'region' : 'dialog'}
      aria-label={t('aiExplanationTitle')}
      sx={{
        width: inline ? '100%' : { xs: '100%', sm: 520 },
        maxWidth: '100vw',
        p: inline ? 0 : { xs: 7, sm: 8 },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={4}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="textXS"
            color="text.secondary"
            fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {t('aiExplanationEyebrow')}
          </Typography>
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={2}
            sx={{ mt: 2 }}
          >
            <Typography variant="displayXS"
              fontWeight={700}>
              {formatNumber(recommendation)}
            </Typography>
            {displayOrderUom && (
              <Typography variant="textSM"
                color="text.secondary"
                fontWeight={600}>
                {displayOrderUom}
              </Typography>
            )}
          </Stack>
          {explanation?.deliveryDate && (
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              {t('aiExplanationForDelivery', {
                date: dayjs(explanation.deliveryDate).format('DD MMM YYYY'),
              })}
            </Typography>
          )}
        </Box>

        {!inline && (
          <IconButton
            aria-label={t('close')}
            size="small"
            onClick={close}
            sx={{ mt: -1, mr: -1 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 11 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="info"
          sx={{ mt: 8 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && explanation && (
        <>
          {quantityWasOverridden && displayedFinalQuantity != null && (
            <Box
              sx={{
                mt: 8,
                px: 6,
                py: 5,
                borderRadius: 3,
                bgcolor: 'action.hover',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={4}
              >
                <Typography variant="textSM"
                  fontWeight={650}>
                  {t('aiExplanationFinalOrder', {
                    quantity: formatNumber(displayedFinalQuantity),
                    uom: displayOrderUom,
                  })}
                </Typography>
                <Chip size="small"
                  label={t('aiEdited')}
                  sx={{ fontWeight: 600 }} />
              </Stack>
              <Typography
                variant="textXS"
                color="text.secondary"
                sx={{ display: 'block', mt: 2 }}
              >
                {t('aiExplanationOverriddenNote')}
              </Typography>
            </Box>
          )}

          {hasCalculationBreakdown && calculationSteps.length > 0 && (
            <Box sx={{ mt: 9 }}>
              <Typography
                variant="textXS"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  display: 'block',
                  mb: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {t('aiExplanationCalculationTitle')}
              </Typography>

              <Stack>
                {calculationSteps.map((step, index) => (
                  <Stack
                    key={`${step.label}-${index}`}
                    direction="row"
                    alignItems="flex-start"
                    spacing={4}
                    sx={{ py: 4 }}
                  >
                    <Typography
                      aria-hidden="true"
                      variant="textXS"
                      color="text.disabled"
                      fontWeight={700}
                      sx={{ width: 28, flex: '0 0 28px', pt: 1 }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row"
                        justifyContent="space-between"
                        spacing={6}>
                        <Typography variant="textSM"
                          color="text.primary"
                          fontWeight={550}>
                          {step.label}
                        </Typography>
                        <Typography
                          variant="textSM"
                          fontWeight={700}
                          sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                          {step.value}
                        </Typography>
                      </Stack>
                      {step.detail && (
                        <Typography
                          variant="textXS"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 2, maxWidth: 360 }}
                        >
                          {step.detail}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          <Box sx={{ mt: 9 }}>
            <Typography
              variant="textXS"
              color="text.secondary"
              fontWeight={700}
              sx={{
                display: 'block',
                mb: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {t('aiExplanationInputsTitle')}
            </Typography>

            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 3,
                px: 6,
                py: 4,
              }}
            >
              {capturedInputs.map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={6}
                  sx={{ py: 3 }}
                >
                  <Typography variant="textSM"
                    color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography
                    variant="textSM"
                    fontWeight={650}
                    sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    {formatNumber(row.value)}{row.uom ? ` ${row.uom}` : ''}
                  </Typography>
                </Stack>
              ))}

              {explanation.uomConversionFactor > 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={6}
                  sx={{ py: 3 }}
                >
                  <Typography variant="textSM"
                    color="text.secondary">
                    {t('aiExplanationCaseConversion')}
                  </Typography>
                  <Typography
                    variant="textSM"
                    fontWeight={650}
                    sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    {t('aiExplanationCaseConversionValue', {
                      orderUom: displayOrderUom,
                      factor: formatNumber(explanation.uomConversionFactor),
                      primaryUom,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>

          {explanation.capturedAt && (
            <Typography
              variant="textXS"
              color="text.secondary"
              sx={{ display: 'block', mt: 5 }}
            >
              {t('aiExplanationRecordedAt', {
                date: dayjs(explanation.capturedAt).format('DD MMM YYYY, HH:mm'),
              })}
            </Typography>
          )}

          {hasForecast && (
            <>
              <Divider sx={{ mt: 8, mb: 4 }} />
              <ButtonBase
                onClick={() => setShowForecast((value) => !value)}
                aria-expanded={showForecast}
                sx={{
                  width: '100%',
                  minHeight: 44,
                  borderRadius: 2,
                  px: 2,
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="textSM"
                  fontWeight={650}>
                  {t('aiExplanationForecastDetails')}
                </Typography>
                <ExpandMoreRoundedIcon
                  fontSize="small"
                  sx={{
                    transform: showForecast ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform'),
                  }}
                />
              </ButtonBase>

              <Collapse in={showForecast}
                timeout="auto"
                unmountOnExit>
                <Box sx={{ mt: 4, overflowX: 'auto' }}>
                  <Table size="small"
                    aria-label={t('aiExplanationForecastDetails')}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ px: 5, py: 4 }}>
                          {t('aiExplanationProjectionDate')}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ px: 5, py: 4 }}>
                          {t('aiExplanationProjectionStockOnHand')}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ px: 5, py: 4 }}>
                          {t('aiExplanationProjectionIncoming')}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ px: 5, py: 4 }}>
                          {t('aiExplanationProjectionDemand')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {explanation.projection?.map((row) => (
                        <TableRow key={row.date}>
                          <TableCell sx={{ px: 5, py: 4 }}>
                            {dayjs(row.date).format('DD MMM')}
                          </TableCell>
                          <TableCell align="right"
                            sx={{ px: 5, py: 4 }}>
                            {formatNumber(row.quantityOnHand)}
                          </TableCell>
                          <TableCell align="right"
                            sx={{ px: 5, py: 4 }}>
                            {formatNumber(row.incomingQuantity)}
                          </TableCell>
                          <TableCell align="right"
                            sx={{ px: 5, py: 4 }}>
                            {formatNumber(row.predictedDemandQuantity)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </>
          )}
        </>
      )}
    </Box>
  );

  if (inline) {
    return content;
  }

  const trigger = triggerVariant === 'help'
    ? (
      <Tooltip title={t('whyDidAiOrderThis')}>
        <IconButton
          onClick={handleClick}
          aria-label={t('whyDidAiOrderThis')}
          aria-haspopup="dialog"
          aria-expanded={open}
          size="small"
          sx={{
            width: 32,
            height: 32,
            p: 1,
            bgcolor: 'background.paper',
            color: 'text.secondary',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
            transition: theme.transitions.create(['background-color', 'color', 'box-shadow']),
            '&:hover': {
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: '0 2px 6px rgba(15, 23, 42, 0.18)',
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <HelpOutlineRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    )
    : (
      <ButtonBase
        onClick={handleClick}
        aria-label={t('whyDidAiOrderThis')}
        aria-haspopup={onClick ? undefined : 'dialog'}
        aria-expanded={onClick ? undefined : open}
        sx={{
          minHeight: 26,
          px: 1,
          borderRadius: 999,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          gap: 0.5,
          whiteSpace: 'nowrap',
          transition: theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow']),
          '&:hover': {
            bgcolor: 'action.hover',
            color: 'text.primary',
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
        <Typography variant="textXS"
          fontWeight={650}>
          {recommendation != null
            ? t('aiRecommendationShort', { value: formatNumber(recommendation) })
            : t('aiInsight')}
        </Typography>
        {quantityWasOverridden && (
          <Typography variant="textXS"
            color="text.secondary">
            · {t('aiEdited')}
          </Typography>
        )}
      </ButtonBase>
    );

  return (
    <>
      {trigger}

      {!onClick && isMobile && (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              maxHeight: '90vh',
              overflowY: 'auto',
              '&&': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
            },
          }}
        >
          {content}
        </Drawer>
      )}

      {!onClick && !isMobile && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)',
                overflow: 'hidden',
              },
            },
          }}
        >
          {content}
        </Popover>
      )}
    </>
  );
};

export default AiOrderExplanationButton;

